import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Settings2 } from 'lucide-react';
import eodhd from '../services/eodhd';
import { expertApi } from '../services/expertApi';
import LoadingSpinner from './LoadingSpinner';
import AssetLogo from './AssetLogo';

interface MarketHeatmapProps {
  showStocks?: boolean;
}

const HEATMAP_EXCHANGES = [
  { code: 'US',       label: 'US Markets',    flag: '🇺🇸' },
  { code: 'JSE',      label: 'JSE (SA)',       flag: '🇿🇦' },
  { code: 'LSE',      label: 'London (LSE)',   flag: '🇬🇧' },
  { code: 'HK',       label: 'Hong Kong',      flag: '🇭🇰' },
  { code: 'ASX',      label: 'ASX (AU)',       flag: '🇦🇺' },
];

const DEFAULT_EXCHANGES = ['US', 'JSE'];

const MarketHeatmap: React.FC<MarketHeatmapProps> = ({ showStocks = true }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedSector, setSelectedSector] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);

  // Load user's saved exchange preferences
  const { data: prefs } = useQuery({
    queryKey: ['banker-preferences'],
    queryFn: () => expertApi.getBankerPreferences(),
    staleTime: 5 * 60 * 1000,
  });

  const activeExchanges: string[] = prefs?.heatmap_exchanges?.length
    ? prefs.heatmap_exchanges
    : DEFAULT_EXCHANGES;

  // Save preference mutation
  const saveMutation = useMutation({
    mutationFn: (exchanges: string[]) =>
      expertApi.updateBankerPreferences({ heatmap_exchanges: exchanges }),
    onSuccess: (data) => {
      queryClient.setQueryData(['banker-preferences'], data);
      queryClient.invalidateQueries({ queryKey: ['heatmap'] });
    },
  });

  // Close picker on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggleExchange = (code: string) => {
    const current = [...activeExchanges];
    if (current.includes(code)) {
      if (current.length === 1) return; // must keep at least 1
      saveMutation.mutate(current.filter((c) => c !== code));
    } else {
      if (current.length >= 3) return; // max 3
      saveMutation.mutate([...current, code]);
    }
  };

  // Use React Query to fetch heatmap data with automatic caching
  const { data, isLoading, error } = useQuery({
    queryKey: ['heatmap', activeExchanges],
    queryFn: () => eodhd.getHeatmap(activeExchanges),
    staleTime: 15 * 60 * 1000,
    gcTime: 20 * 60 * 1000,
    enabled: activeExchanges.length > 0,
  });

  const getColorByChange = (change: number): string => {
    if (change >= 3) return 'bg-emerald-500';
    if (change >= 2) return 'bg-emerald-600';
    if (change >= 1) return 'bg-emerald-700';
    if (change >= 0.5) return 'bg-emerald-800';
    if (change > 0) return 'bg-emerald-900';
    if (change === 0) return 'bg-slate-700';
    if (change > -0.5) return 'bg-red-900';
    if (change > -1) return 'bg-red-800';
    if (change > -2) return 'bg-red-700';
    if (change > -3) return 'bg-red-600';
    return 'bg-red-500';
  };

  const getTextColorByChange = (change: number): string => {
    if (Math.abs(change) >= 1) return 'text-white';
    return 'text-slate-300';
  };

  const handleSectorClick = (sectorName: string) => {
    setSelectedSector(selectedSector === sectorName ? null : sectorName);
  };

  const handleStockClick = (symbol: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/asset/${symbol}`);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <LoadingSpinner />
        <span className="ml-2 text-sm text-slate-400">Loading market data...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-8">
        <span className="text-sm text-red-400">Failed to load heatmap data</span>
      </div>
    );
  }

  if (!data || !data.sectors || data.sectors.length === 0) {
    return (
      <div className="flex items-center justify-center py-8">
        <span className="text-sm text-slate-400">No heatmap data available</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header row: active exchange badges + gear picker */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex gap-1 flex-wrap">
          {activeExchanges.map((code) => {
            const ex = HEATMAP_EXCHANGES.find((e) => e.code === code);
            return ex ? (
              <span key={code} className="text-xs bg-slate-700 text-slate-300 px-2 py-0.5 rounded">
                {ex.flag} {ex.label}
              </span>
            ) : null;
          })}
        </div>

        {/* Exchange picker */}
        <div className="relative ml-auto" ref={pickerRef}>
          <button
            onClick={() => setPickerOpen((v) => !v)}
            className="p-1.5 text-slate-400 hover:text-teal-400 hover:bg-slate-700 rounded transition-colors"
            title="Change exchanges (max 3)"
          >
            <Settings2 size={14} />
          </button>

          {pickerOpen && (
            <div className="absolute right-0 top-7 z-20 w-52 bg-slate-800 border border-slate-700 rounded-lg shadow-xl p-3 space-y-1">
              <p className="text-xs text-slate-400 mb-2">Select up to 3 exchanges</p>
              {HEATMAP_EXCHANGES.map((ex) => {
                const active = activeExchanges.includes(ex.code);
                const maxed = !active && activeExchanges.length >= 3;
                return (
                  <button
                    key={ex.code}
                    onClick={() => !maxed && toggleExchange(ex.code)}
                    disabled={maxed}
                    className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm transition-colors ${
                      active
                        ? 'bg-teal-500/20 text-teal-400'
                        : maxed
                        ? 'text-slate-600 cursor-not-allowed'
                        : 'text-slate-300 hover:bg-slate-700'
                    }`}
                  >
                    <span>{ex.flag}</span>
                    <span className="flex-1 text-left">{ex.label}</span>
                    {active && <span className="w-2 h-2 rounded-full bg-teal-400" />}
                  </button>
                );
              })}
              {saveMutation.isPending && (
                <p className="text-xs text-slate-500 text-center pt-1">Saving...</p>
              )}
            </div>
          )}
        </div>

        {data?.last_updated && (
          <span className="text-xs text-slate-500">
            {new Date(data.last_updated * 1000).toLocaleTimeString()}
            {data.cached && ' (cached)'}
          </span>
        )}
      </div>

      {/* Heatmap Grid */}
      <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
        {data.sectors.map((sector) => (
          <div key={sector.symbol} className="space-y-1">
            {/* Sector Tile */}
            <div
              onClick={() => showStocks && handleSectorClick(sector.name)}
              className={`${getColorByChange(sector.change)} rounded-lg p-3 cursor-pointer transition-all hover:opacity-90 hover:scale-[1.02] ${
                selectedSector === sector.name ? 'ring-2 ring-teal-400' : ''
              }`}
            >
              <div className={`${getTextColorByChange(sector.change)}`}>
                <p className="text-xs font-medium truncate">{sector.name}</p>
                <p className="text-lg font-bold font-mono">
                  {sector.change >= 0 ? '+' : ''}{sector.change.toFixed(2)}%
                </p>
              </div>
            </div>

            {/* Expanded Stock List */}
            {showStocks && selectedSector === sector.name && sector.stocks && (
              <div className="bg-slate-800 rounded-lg p-2 border border-slate-700 space-y-1 animate-in slide-in-from-top-2">
                {sector.stocks.map((stock) => (
                  <div
                    key={stock.symbol}
                    onClick={(e) => handleStockClick(stock.symbol, e)}
                    className="flex items-center gap-2 py-1 px-2 hover:bg-slate-700 rounded cursor-pointer transition-colors"
                  >
                    <AssetLogo symbol={stock.symbol} name={stock.name} size="xs" />
                    <span className="text-xs text-slate-300 truncate flex-1">{stock.name}</span>
                    <span className={`text-xs font-mono ${stock.change >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {stock.change >= 0 ? '+' : ''}{stock.change.toFixed(2)}%
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-1 pt-2">
        <span className="text-xs text-slate-500 mr-2">Change:</span>
        <div className="flex gap-0.5">
          <div className="w-6 h-3 bg-red-500 rounded-l" title="-3%+" />
          <div className="w-6 h-3 bg-red-700" title="-1% to -2%" />
          <div className="w-6 h-3 bg-red-900" title="0% to -1%" />
          <div className="w-6 h-3 bg-slate-700" title="0%" />
          <div className="w-6 h-3 bg-emerald-900" title="0% to 1%" />
          <div className="w-6 h-3 bg-emerald-700" title="1% to 2%" />
          <div className="w-6 h-3 bg-emerald-500 rounded-r" title="3%+" />
        </div>
        <span className="text-xs text-slate-500 ml-2">-3% to +3%</span>
      </div>
    </div>
  );
};

export default MarketHeatmap;
